import JSZip from 'jszip';
import {
  pairSignalsWithEventXmls,
  type PairedEventXml,
} from './pair-signal-events';

export type SupportedEdfExtensions = 'edf' | 'wmedf';
export type TherapyContainerType =
  | 'psdat'
  | 'pdat'
  | 'pvdat'
  | 'pldat'
  | 'custom';

export type FileUploadData = {
  edfFiles: File[];
  // Full container paths, index-aligned with edfFiles. Required to match
  // processor-generated manifest entries with backend archive entries.
  edfArchivePaths?: string[];
  // Loose custom uploads only, index-aligned with edfFiles. Container archives
  // don't extract XML in the browser: their events come from the backend
  // /events endpoints.
  eventXmls?: (PairedEventXml | undefined)[];
  // Surfaced to the user as a warning.
  unmatchedEventXmlNames?: string[];
  extensions: SupportedEdfExtensions[];
  containerType: TherapyContainerType;
  sourceFileName: string;
  sourceFile?: File;
};

export type ContainerDay = {
  date: string;
  signals: ContainerSignalFile[];
};

export type ContainerParseResult = {
  containerName: string;
  containerType: Exclude<TherapyContainerType, 'custom'>;
  days: ContainerDay[];
};

type ContainerSignalFile = {
  file: File;
  archivePath: string;
};

type SupportedFileUploadExtensions =
  | 'psdat'
  | 'pdat'
  | 'pvdat'
  | 'pldat'
  | 'edf'
  | 'wmedf';

export const FILE_INPUT_ACCEPT = '.psdat,.pdat,.pvdat,.pldat,.edf,.wmedf,.xml';
export const MAX_UPLOAD_FILE_SIZE_BYTES = 1_610_612_736;
export const MAX_UPLOAD_FILE_SIZE_LABEL = '1.5 GB';

const SUPPORTED_FILE_REGEX = /\.(psdat|pdat|pvdat|pldat|edf|wmedf|xml)$/i;

export const hasDraggedFiles = (e: DragEvent) =>
  e.dataTransfer?.types.includes('Files') ?? false;

export type FileUploadValidationErrorCode =
  | 'empty-selection'
  | 'unsupported-file-format'
  | 'file-too-large'
  | 'invalid-upload'
  | 'corrupted-file';

export class FileUploadValidationError extends Error {
  constructor(
    public readonly code: FileUploadValidationErrorCode,
    message: string,
    public readonly fileNames: string[] = [],
  ) {
    super(message);
    this.name = 'FileUploadValidationError';
  }
}

export function isFileUploadValidationError(
  error: unknown,
): error is FileUploadValidationError {
  return error instanceof FileUploadValidationError;
}

const getExtension = (fileName: string): SupportedFileUploadExtensions => {
  const lower = fileName.toLowerCase();

  if (lower.endsWith('.wmedf')) return 'wmedf';
  if (lower.endsWith('.psdat')) return 'psdat';
  if (lower.endsWith('.pvdat')) return 'pvdat';
  if (lower.endsWith('.pldat')) return 'pldat';
  if (lower.endsWith('.pdat')) return 'pdat';
  if (lower.endsWith('.edf')) return 'edf';

  throw new Error(`Unsupported file type: ${fileName}`);
};

const getContainerType = (
  extensions: SupportedFileUploadExtensions[],
): TherapyContainerType => {
  return extensions.includes('psdat')
    ? 'psdat'
    : extensions.includes('pvdat')
      ? 'pvdat'
      : extensions.includes('pldat')
        ? 'pldat'
        : extensions.includes('pdat')
          ? 'pdat'
          : 'custom';
};

const toSignalFiles = async (entries: JSZip.JSZipObject[]) => {
  const signals = await Promise.all(
    entries.map(async (entry): Promise<ContainerSignalFile> => {
      const baseName = entry.name.split('/').pop() ?? entry.name;
      return {
        file: await toFileFromZipEntry(entry, baseName),
        archivePath: entry.name.replace(/\\/g, '/'),
      };
    }),
  );

  // An aborted recording can leave a zero-byte WMEDF next to its (truncated)
  // event XML; pTS drops such records outright (ZipImporter.GetRecords), so
  // skip them instead of failing the manifest build for the whole archive.
  return signals.filter((signal) => signal.file.size > 0);
};

const toFileFromZipEntry = async (
  entry: JSZip.JSZipObject,
  fileName: string,
) => {
  const blob = await entry.async('blob');

  return new File([blob], fileName, { type: 'application/octet-stream' });
};

/** Strips the last .ext from a filename. */
const stripLastExtension = (name: string) => {
  const dotIdx = name.lastIndexOf('.');
  return dotIdx > 0 ? name.slice(0, dotIdx) : name;
};

const PSDAT_SIGNAL_PATH_REGEX = /^signals\/(\d{8})\/.+\.(wmedf|edf)$/i;

const PDAT_SIGNAL_PATH_REGEX =
  /^mnt\/flash\/data\/therapy\/signals\/(\d{8})\/.+\.(wmedf|edf)$/i;

// pvdat (P3.2 / pVent): YYYY-MM-DD/SEQ/<files>
const PVDAT_SIGNAL_PATH_REGEX = /^(\d{4}-\d{2}-\d{2})\/\d+\/.+\.(wmedf|edf)$/i;

// pldat (P5): YYYY-MM-DD/SEQ/<edf files>
const PLDAT_SIGNAL_PATH_REGEX = /^(\d{4}-\d{2}-\d{2})\/\d+\/.+\.edf$/i;

const SIGNAL_REGEX_BY_CONTAINER: Record<
  Exclude<TherapyContainerType, 'custom'>,
  RegExp
> = {
  psdat: PSDAT_SIGNAL_PATH_REGEX,
  pdat: PDAT_SIGNAL_PATH_REGEX,
  pvdat: PVDAT_SIGNAL_PATH_REGEX,
  pldat: PLDAT_SIGNAL_PATH_REGEX,
};

const collectDaySignalEntries = (zip: JSZip, signalRegex: RegExp) => {
  const dayEntriesMap = new Map<string, JSZip.JSZipObject[]>();

  zip.forEach((path, entry) => {
    if (entry.dir) return;

    const normalizedPath = path.replace(/\\/g, '/');

    const signalMatch = normalizedPath.match(signalRegex);
    if (!signalMatch) return;

    const date = signalMatch[1];
    const entries = dayEntriesMap.get(date);
    if (entries) {
      entries.push(entry);
    } else {
      dayEntriesMap.set(date, [entry]);
    }
  });

  return dayEntriesMap;
};

const buildContainerDays = async (
  dayEntriesMap: Map<string, JSZip.JSZipObject[]>,
): Promise<ContainerDay[]> => {
  const days: ContainerDay[] = [];

  for (const [date, signalEntries] of dayEntriesMap.entries()) {
    const signals = await toSignalFiles(signalEntries);

    days.push({ date, signals });
  }

  days.sort((a, b) => a.date.localeCompare(b.date));

  return days;
};

const parseTherapyContainer = async (
  containerFile: File,
  containerType: Exclude<TherapyContainerType, 'custom'>,
): Promise<ContainerParseResult> => {
  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(containerFile);
  } catch {
    throw new FileUploadValidationError(
      'corrupted-file',
      `Data from ${containerFile.name} can't be displayed.`,
      [containerFile.name],
    );
  }

  const signalRegex = SIGNAL_REGEX_BY_CONTAINER[containerType];
  const dayEntriesMap = collectDaySignalEntries(zip, signalRegex);
  const days = await buildContainerDays(dayEntriesMap);

  return {
    containerName: stripLastExtension(containerFile.name),
    containerType,
    days,
  };
};

export const buildFileUploadData = async (
  files: File[],
): Promise<FileUploadData> => {
  if (files.length === 0) {
    throw new FileUploadValidationError(
      'empty-selection',
      'No files selected.',
    );
  }

  const unsupported = files.filter((f) => !SUPPORTED_FILE_REGEX.test(f.name));
  if (unsupported.length > 0) {
    throw new FileUploadValidationError(
      'unsupported-file-format',
      `Unsupported file(s): ${unsupported.map((f) => f.name).join(', ')}`,
      unsupported.map((f) => f.name),
    );
  }

  const tooLarge = files.filter(
    (file) => file.size > MAX_UPLOAD_FILE_SIZE_BYTES,
  );
  if (tooLarge.length > 0) {
    throw new FileUploadValidationError(
      'file-too-large',
      `File exceeds maximum allowed size (${MAX_UPLOAD_FILE_SIZE_LABEL}).`,
      tooLarge.map((file) => file.name),
    );
  }

  const xmlFiles = files.filter((f) => /\.xml$/i.test(f.name));
  const nonXmlFiles = files.filter((f) => !/\.xml$/i.test(f.name));
  const extensions = nonXmlFiles.map((f) => getExtension(f.name));
  const containerType = getContainerType(extensions);

  if (containerType !== 'custom') {
    if (files.length !== 1) {
      throw new FileUploadValidationError(
        'invalid-upload',
        `Please drop exactly one .${containerType} file at a time.`,
        files.map((file) => file.name),
      );
    }

    const result = await parseTherapyContainer(files[0], containerType);

    if (result.days.length === 0) {
      throw new FileUploadValidationError(
        'corrupted-file',
        `No therapy sessions found in .${result.containerType}.`,
        [files[0].name],
      );
    }

    const signals = result.days.flatMap((day) => day.signals);
    if (signals.length === 0) {
      throw new FileUploadValidationError(
        'corrupted-file',
        `No signal files found in .${result.containerType}.`,
        [files[0].name],
      );
    }

    return {
      edfFiles: signals.map((signal) => signal.file),
      edfArchivePaths: signals.map((signal) => signal.archivePath),
      extensions: result.containerType === 'pldat' ? ['edf'] : ['wmedf'],
      containerType: result.containerType,
      sourceFileName: files[0].name,
      sourceFile: files[0],
    };
  }

  if (nonXmlFiles.length === 0) {
    throw new FileUploadValidationError(
      'invalid-upload',
      'No signal files selected.',
      files.map((file) => file.name),
    );
  }

  const pairing = await pairSignalsWithEventXmls(nonXmlFiles, xmlFiles);

  return {
    edfFiles: nonXmlFiles,
    eventXmls: xmlFiles.length > 0 ? pairing.eventXmls : undefined,
    unmatchedEventXmlNames:
      pairing.unmatchedXmlNames.length > 0
        ? pairing.unmatchedXmlNames
        : undefined,
    extensions: extensions as SupportedEdfExtensions[],
    containerType,
    sourceFileName: nonXmlFiles.map((file) => file.name).join(', '),
  };
};
